import { Injectable } from '@angular/core';
import { ClientConfigurationApiModel, ConfigEntryApiModel, PlatformApiModel } from '@cxb/content-stb-api';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EntityDataLoaderService } from '../../../data';
import { ClientConfig } from './client-config.interface';

@Injectable({
  providedIn: 'root',
})
export class ClientConfigService {
  private clientConfig: ClientConfig;

  public configAvailable$ = new BehaviorSubject(false);

  constructor(private readonly entityDataLoaderService: EntityDataLoaderService) {}

  public init() {
    return this.entityDataLoaderService.getClientConfiguration(PlatformApiModel.Web).pipe(
      tap((data: ClientConfigurationApiModel) => {
        this.clientConfig = this.mapClientConfig(data);
        this.configAvailable$.next(true);
      })
    );
  }

  public get contentTrayGlobalElementLimit(): number {
    return this.clientConfig.contentTrayGlobalElementLimit;
  }

  public get listInitialRangeLimit(): number {
    return this.clientConfig.listPagePageSize;
  }

  public get listRangeLimit(): number {
    return this.clientConfig.listViewPageSize;
  }

  public get mpxAllMediaFeedUrl(): string {
    return this.clientConfig.mpxAllMediaFeedUrl;
  }

  public get mpxAllProgramsFeedUrl(): string {
    return this.clientConfig.mpxAllProgramsFeedUrl;
  }

  public get mpxAllSeriesFeedUrl(): string {
    return this.clientConfig.mpxAllSeriesFeedUrl;
  }

  public get mpxAllSeasonsFeedUrl(): string {
    return this.clientConfig.mpxAllSeasonsFeedUrl;
  }

  public get mpxAllEpisodesFeedUrl(): string {
    return this.clientConfig.mpxAllEpisodesFeedUrl;
  }

  public get clevertapClientId(): string {
    return this.clientConfig.clevertapClientId ?? null;
  }

  public get clevertapRegion(): string {
    return this.clientConfig.clevertapRegion ?? null;
  }

  public get clevertapTargetDomain(): string {
    return this.clientConfig.clevertapTargetDomain ?? null;
  }

  public get mpxAccountId(): string {
    return this.clientConfig.mpxAccountId;
  }

  public get mpxAccountIdNumber(): string {
    return this.mpxAccountId.split('/')[this.mpxAccountId.split('/').length - 1] || '';
  }

  public get playerControlsVisibilityMilliseconds(): number {
    return this.clientConfig.playerControlsVisibilityMilliseconds;
  }

  public get playerParentalRatingVisibilityMilliseconds(): number {
    return this.clientConfig.playerParentalRatingVisibilityMilliseconds;
  }

  public get gatewayClientId(): string {
    return this.clientConfig.gatewayClientId;
  }

  public get gatewayBaseUrl(): string {
    return this.clientConfig.osnGatewayBaseUrlV2;
  }

  public get gatewayAuthBaseUrl(): string {
    return this.clientConfig.gatewayAuthBaseUrl;
  }

  public get gatewaySignupFacadeBaseUrl(): string {
    return this.clientConfig.gatewaySignupFacadeBaseUrl;
  }

  public get gatewayB2CPolicies() {
    return {
      names: {
        signUpSignIn: 'B2C_1A_OsnStandardSigninSignupRelyingParty',
        forgotPassword: 'B2C_1A_OsnStandardPasswordResetRelyingParty',
      },
      authorities: {
        signUpSignIn: {
          signInAuthority: this.clientConfig.gatewaySignUpSignInAuthority,
          subscribeAuthority: this.clientConfig.gatewaySubscribeAuthority,
        },
        forgotPassword: {
          authority: this.clientConfig.gatewayForgotPasswordAuthority,
        },
      },
    };
  }

  public get gatewayRequiredScopes(): Array<string> {
    return this.clientConfig.gatewayRequiredScopes;
  }

  public get gatewayEndpoints() {
    const signupApiBaseUrl = 'signup-landing-support-facade/v1/';
    const signupServiceBase = 'signup-funnel/';

    const signupAppBaseUrl = new URL(signupApiBaseUrl, this.gatewaySignupFacadeBaseUrl);
    const authBaseUrl = new URL(signupServiceBase, this.gatewayAuthBaseUrl);

    return {
      mpxStreamingTokenGeneration: new URL('generate-streaming-token', this.gatewayBaseUrl).href,
      mpxNonStreamingTokenGeneration: new URL('generate-mpx-auth-token', this.gatewayBaseUrl).href,
      profileList: new URL('profiles', this.gatewayBaseUrl).href,
      passwordValidation: new URL('accounts/validate-password', this.gatewayBaseUrl).href,
      idTokenHint: new URL('generateJwt', this.gatewayBaseUrl).href,
      accounts: new URL('accounts', this.gatewayBaseUrl).href,
      countries: new URL('countries', this.gatewayBaseUrl).href,
      listOfSubscriptionPackages: new URL('subscription-packages-sellable', this.gatewayBaseUrl).href,
      listOfAllSubscriptionPackages: new URL('subscription-packages-all-sellable', this.gatewayBaseUrl).href,
      qrAuthenticate: new URL('qr/:qrCode/authenticate', this.gatewayBaseUrl).href,
      loginWithOneTimeCode: new URL('login/one-time', signupAppBaseUrl.href).href,
      loginForOneTimeCode: new URL('login/for-one-time', signupAppBaseUrl.href).href,
      loginWithCode: new URL('login/code', signupAppBaseUrl.href).href,
      loginWithRefreshToken: new URL('redeem', this.gatewayBaseUrl).href,
      loginUrl: new URL('login/pure', authBaseUrl.href).href,
      settingsLoginUrl: new URL('login/settings', authBaseUrl.href).href,
      signupUrl: new URL('', authBaseUrl.href).href,
      passwordResetUrl: new URL('forgot-password', authBaseUrl.href).href,
      validateCredential: new URL('credentials', signupAppBaseUrl).href,
      validateVoucherCode: new URL('promotions/voucher', signupAppBaseUrl).href,
      validateCard: new URL('promotions/card', signupAppBaseUrl).href,
      createProspectCustomer: new URL('create-prospect-customer', signupAppBaseUrl).href,
      createLeadCustomer: new URL('create-lead-customer', signupAppBaseUrl).href,
      fawryDetails: new URL('customers/fawry-details', this.gatewayBaseUrl).href,
      subscriptionGroupsOfUser: new URL('v1/subscription-groups', this.gatewayStreamingAppFacadeUrl).href,
    };
  }

  public get gatewaySubscriptionKey() {
    return this.clientConfig.gatewaySubscriptionKey;
  }

  public get playerLicense(): string {
    return this.clientConfig.playerLicense;
  }

  public get fairplayCertificateURL(): string {
    return this.clientConfig.fairplayCertificateURL;
  }

  public getBrandImageUrl(brand: 'brandLogoHBO' | 'brandLogoDISNEY' | 'brandLogoPPLUS' | 'brandLogoABCStudios'): string {
    return this.clientConfig[brand];
  }

  public get mpxBookmarkServiceBaseUrl(): string {
    return this.clientConfig.mpxBookmarkServiceBaseUrl;
  }

  public get playerPlaybackBookmarksUpdateIntervalMilliseconds(): number {
    return this.clientConfig.playerPlaybackBookmarksUpdateIntervalSeconds * 1000;
  }

  public get playerScrubBookmarksUpdateIntervalMilliseconds(): number {
    return this.clientConfig.playerScrubBookmarksUpdateIntervalSeconds * 1000;
  }

  public get localizationBaseUrl(): string {
    return this.clientConfig.localizationBaseUrl;
  }

  public get gatewaySignupRouterAuthFlow() {
    return this.clientConfig.gatewaySignupRouterAuthFlow;
  }

  public get gatewaySignupRouterUnauthFlow() {
    return this.clientConfig.gatewaySignupRouterUnauthFlow;
  }

  public get seriesNextEpisodePlayDelay() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.clientConfig.seriesNextEpisodePlayDelay ?? 8000;
  }

  public getAppStoreLinks(key: 'appStoreApple' | 'appStoreGoogle' | 'appStoreHuawei'): string {
    return this.clientConfig[key];
  }

  public get mpxSearchFeedUrl(): string {
    return this.clientConfig.mpxSearchFeedUrl;
  }

  public get mpxSearchLatestFeedUrl(): string {
    return this.clientConfig.mpxSearchLatestFeedUrl;
  }

  public get mpxSearchPopularFeedUrl(): string {
    return this.clientConfig.mpxSearchPopularFeedUrl;
  }

  public get mpxSearchTrendingFeedUrl(): string {
    return this.clientConfig.mpxSearchTrendingFeedUrl;
  }

  public get mpxAllBrandsFeedUrl(): string {
    return this.clientConfig.mpxAllBrandsFeedUrl;
  }

  public get mpxAllGenresFeedUrl(): string {
    return this.clientConfig.mpxAllGenresFeedUrl;
  }

  public get mpxUserListServiceBaseUrl(): string {
    return this.clientConfig.mpxUserListServiceBaseUrl;
  }

  public get mpxUserListServiceUrl(): string {
    return new URL('userprofile/data/UserList', this.mpxUserListServiceBaseUrl).href;
  }

  public get mpxUserListItemServiceUrl(): string {
    return new URL('userprofile/data/UserListItem', this.mpxUserListServiceBaseUrl).href;
  }

  public get settingsWebappManageProfileUrl(): string {
    return this.clientConfig.settingsWebappManageProfileUrl;
  }

  public get settingsWebappAccountSettingsUrl(): string {
    return this.clientConfig.settingsWebappAccountSettingsUrl;
  }

  public get settingsWebappPaymentSettingsUrl(): string {
    return this.clientConfig.settingsWebappPaymentSettingsUrl;
  }

  public get accountDeletionUrl(): string {
    return this.clientConfig.accountDeletionUrl;
  }

  public get heroAutomaticRollDelayMilliseconds(): number {
    return this.clientConfig.heroAutomaticRollDelayMilliseconds;
  }

  public get convivaCustomerKey(): string {
    return this.clientConfig.convivaCustomerKey;
  }

  public get myListTrayItemsLimit(): number {
    return this.clientConfig.myListTrayItemsLimit;
  }

  public get bookmarksTrayItemsLimit(): number {
    return this.clientConfig.bookmarksTrayItemsLimit;
  }

  public get gatewayPermittedPlaybackUserStatuses(): string[] {
    return this.clientConfig.gatewayPermittedPlaybackUserStatuses;
  }

  public get addNewProfileUserStatuses(): string[] {
    return this.clientConfig.addNewProfileUserStatuses;
  }

  public get privacyPolicyEn(): string {
    return this.clientConfig.privacyPolicyEn;
  }

  public get privacyPolicyAr(): string {
    return this.clientConfig.privacyPolicyAr;
  }

  public get termsAndConditionsUrlEn(): string {
    return this.clientConfig.termsAndConditionsUrlEn;
  }

  public get termsAndConditionsUrlAr(): string {
    return this.clientConfig.termsAndConditionsUrlAr;
  }

  public get contactUsUrlEn(): string {
    return this.clientConfig.contactUsUrlEn;
  }

  public get contactUsUrlAr(): string {
    return this.clientConfig.contactUsEmailAddress;
  }

  public getContactPhoneNumberByCountryCode(country): string {
    switch (country) {
      case 'QA':
      case 'QAT':
        return this.clientConfig.contactNumberQAT;
      case 'SA':
      case 'SAU':
      case 'KSA':
        return this.clientConfig.contactNumberKSA;
      case 'KW':
      case 'KWT':
        return this.clientConfig.contactNumberKWT;
      case 'EG':
      case 'EGY':
        return this.clientConfig.contactNumberEGY;
      case 'BH':
      case 'BHR':
      case 'BAH':
        return this.clientConfig.contactNumberBAH;
      case 'JO':
      case 'JOR':
        return this.clientConfig.contactNumberJOR;
      case 'AE':
      case 'ARE':
      case 'UAE':
      default:
        return this.clientConfig.contactNumberUAE;
    }
  }

  public get seoFeedUrl(): string {
    return this.clientConfig.seoFeedUrl;
  }

  public get recommendationLearnVodPlayedPercentage(): number {
    return this.clientConfig.recommendationLearnVodPlayedPercentage;
  }

  public get mpxPersonUrlPrefix(): string {
    return this.clientConfig.mpxPersonUrlPrefix;
  }

  public get seasonCarouselShowSeasonNumber(): boolean {
    return this.clientConfig.seasonCarouselShowSeasonNumber;
  }

  public get paywallSubscriptionPrice(): number {
    return this.clientConfig.paywallSubscriptionPrice;
  }

  public get paywallSubscriptionCurrency(): string {
    return this.clientConfig.paywallSubscriptionCurrency;
  }

  public get paywallGatewaySubscriptionPackageId(): string {
    return this.clientConfig.paywallGatewaySubscriptionPackageId;
  }

  public get showPopupForChurnedUsers(): boolean {
    return this.clientConfig.showPopupForChurnedUsers;
  }

  public get searchAutocompleteLimit(): number {
    return this.clientConfig.searchAutocompleteLimit;
  }

  public get searchPageSizeMobile(): number {
    return this.clientConfig.searchPageSizeMobile;
  }

  public get searchPageSizeDesktop(): number {
    return this.clientConfig.searchPageSizeDesktop;
  }

  public get marketingHeroTrayScrollPeriodMilliseconds(): number {
    return this.clientConfig.marketingHeroTrayScrollPeriodMilliseconds;
  }

  public get trendingTraySwipeIntervalInMilliseconds(): number {
    return this.clientConfig.trendingTraySwipeIntervalInMilliseconds;
  }

  public get trendingTrayAutoSwipeShouldStartAfterInMilliseconds(): number {
    return this.clientConfig.trendingTrayAutoSwipeShouldStartAfterInMilliseconds;
  }

  public get marketingEmailCollectorScrollInterval(): number {
    return this.clientConfig.marketingEmailCollectorScrollInterval;
  }

  public get enableSeoDebugLogs(): boolean {
    return this.clientConfig.enableSeoDebugLogs ?? false;
  }

  public get continueWatchingTrayProgressMinimumPercent(): number {
    return this.clientConfig.continueWatchingTrayProgressMinimumPercent;
  }

  public get continueWatchingTrayProgressLimitPercent(): number {
    return this.clientConfig.continueWatchingTrayProgressLimitPercent;
  }

  public get playerBufferingGoal(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.clientConfig.playerBufferingGoal ?? 10; // 10 is the castlab default
  }

  public get playerDefaultBitrate(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.clientConfig.playerDefaultBitrate ?? 3400; // 720p bitrate (kbps)
  }

  public get gatewayStreamingAppFacadeUrl(): string {
    return this.clientConfig.gatewayStreamingAppFacadeUrl;
  }

  public get gatewaySignupFunnelFacadeUrl(): string {
    return this.clientConfig.gatewaySignupFunnelFacadeUrl;
  }

  public get gatewaySignupLandingSupportFacadeUrl(): string {
    return this.clientConfig.gatewaySignupLandingSupportFacadeUrl;
  }

  public get mobileProviderSignUpSupportedCountries(): string[] {
    return this.clientConfig.mobileProviderSignUpSupportedCountries ?? [];
  }

  public get signUpWithMobileProviderLink(): string {
    return this.clientConfig.signUpWithMobileProviderLink;
  }

  public getMediaCenterLink(): string {
    return this.clientConfig.footerMediaCenterTargetUrl;
  }

  public getContactUsEmailAddress(): string {
    return this.clientConfig.contactUsEmailAddress;
  }

  public get gatewaySubscriptionPackagePriorityList(): string[] {
    return this.clientConfig.gatewaySubscriptionPackagePriorityList ?? [];
  }

  public get mpxFeedUrlPrefix(): string {
    return this.clientConfig.mpxFeedUrlPrefix;
  }

  public get activeUserCtaPopupSubscriptionPackageId(): string {
    return this.clientConfig.activeUserCtaPopupSubscriptionPackageId;
  }

  public get activeUserAnnualCtaPopupMonthlyPackageIds(): string[] {
    return this.clientConfig.activeUserAnnualCtaPopupMonthlyPackageIds ?? [];
  }
  public get activeUserAnnualCtaPopupYearlyPackageIds(): string[] {
    return this.clientConfig.activeUserAnnualCtaPopupYearlyPackageIds ?? [];
  }

  public get touchstoneServiceUrl(): string {
    return this.clientConfig.touchstoneServiceUrl ?? '';
  }

  public get freeTrialTextEnabled(): boolean {
    return this.clientConfig.freeTrialTextEnabled ?? false;
  }

  private mapClientConfig(data: ClientConfigurationApiModel): ClientConfig {
    return {
      mpxBaseUrl: this.getValueFromKey('mpxBaseUrl', data.configEntries),
      mpxAccountId: this.getValueFromKey('mpxAccountId', data.configEntries),
      mpxBookmarkServiceBaseUrl: this.getValueFromKey('mpxBookmarkServiceBaseUrl', data.configEntries),
      mpxUserListServiceBaseUrl: this.getValueFromKey('mpxUserListServiceBaseUrl', data.configEntries),
      osnGatewayBaseUrlV2: this.getValueFromKey('osnGatewayBaseUrlV2', data.configEntries),
      mpxSearchFeedUrl: this.getValueFromKey('mpxSearchFeedUrl', data.configEntries),
      mpxSearchLatestFeedUrl: this.getValueFromKey('mpxSearchLatestFeedUrl', data.configEntries),
      mpxSearchTrendingFeedUrl: this.getValueFromKey('mpxSearchTrendingFeedUrl', data.configEntries),
      mpxSearchPopularFeedUrl: this.getValueFromKey('mpxSearchPopularFeedUrl', data.configEntries),
      searchSignificantTerms: this.getValueFromKey('searchSignificantTerms', data.configEntries),
      mpxAllProgramsFeedUrl: this.getValueFromKey('mpxAllProgramsFeedUrl', data.configEntries),
      mpxAllMediaFeedUrl: this.getValueFromKey('mpxAllMediaFeedUrl', data.configEntries),
      mpxAllGenresFeedUrl: this.getValueFromKey('mpxAllGenresFeedUrl', data.configEntries),
      mpxAllBrandsFeedUrl: this.getValueFromKey('mpxAllBrandsFeedUrl', data.configEntries),
      mpxAllSeasonsFeedUrl: this.getValueFromKey('mpxAllSeasonsFeedUrl', data.configEntries),
      mpxAllSeriesFeedUrl: this.getValueFromKey('mpxAllSeriesFeedUrl', data.configEntries),
      mpxAllEpisodesFeedUrl: this.getValueFromKey('mpxAllEpisodesFeedUrl', data.configEntries),
      kidsProfileDefaultParentalRating: this.getValueFromKey('kidsProfileDefaultParentalRating', data.configEntries),
      listViewPageSize: this.getValueFromKey('listViewPageSize', data.configEntries),
      playerParentalRatingVisibilityMilliseconds: this.getValueFromKey('playerParentalRatingVisibilityMilliseconds', data.configEntries),
      playerControlsVisibilityMilliseconds: this.getValueFromKey('playerControlsVisibilityMilliseconds', data.configEntries),
      playerPlaybackBookmarksUpdateIntervalSeconds: this.getValueFromKey(
        'playerPlaybackBookmarksUpdateIntervalSeconds',
        data.configEntries
      ),
      playerScrubBookmarksUpdateIntervalSeconds: this.getValueFromKey('playerScrubBookmarksUpdateIntervalSeconds', data.configEntries),
      playerNextEpisodeButtonDurationSeconds: this.getValueFromKey('playerNextEpisodeButtonDurationSeconds', data.configEntries),
      contentTrayGlobalElementLimit: this.getValueFromKey('contentTrayGlobalElementLimit', data.configEntries),
      contentTrayPageSize: this.getValueFromKey('contentTrayPageSize', data.configEntries),
      listPagePageSize: this.getValueFromKey('listPagePageSize', data.configEntries),
      profilePicturesEnabled: this.getValueFromKey('profilePicturesEnabled', data.configEntries),
      profilePicturesDisabled: this.getValueFromKey('profilePicturesDisabled', data.configEntries),
      signInResetUrl: this.getValueFromKey('signInResetUrl', data.configEntries),
      signInContactUsUrl: this.getValueFromKey('signInContactUsUrl', data.configEntries),
      signInSignUpUrl: this.getValueFromKey('signInSignUpUrl', data.configEntries),
      localizationBaseUrl: this.getValueFromKey('localizationRebrandedBaseUrl', data.configEntries),
      brandLogoHBO: this.getValueFromKey('brandLogoHBO', data.configEntries),
      brandLogoDISNEY: this.getValueFromKey('brandLogoDISNEY', data.configEntries),
      brandLogoPPLUS: this.getValueFromKey('brandLogoPPLUS', data.configEntries),
      brandLogoABCStudios: this.getValueFromKey('brandLogoABCStudios', data.configEntries),
      defaultTheme: this.getValueFromKey('defaultTheme', data.configEntries),
      defaultLanguage: this.getValueFromKey('defaultLanguage', data.configEntries),
      gatewaySignUpSignInAuthority: this.getValueFromKey('gatewaySignUpSignInAuthority', data.configEntries),
      gatewaySubscribeAuthority: this.getValueFromKey('gatewaySubscribeAuthority', data.configEntries),
      gatewayRequiredScopes: this.getValueFromKey('gatewayRequiredScopes', data.configEntries),
      gatewayIdTokenHintRequestEndpoint: this.getValueFromKey('gatewayIdTokenHintRequestEndpoint', data.configEntries),
      playerLicense: this.getValueFromKey('playerLicense', data.configEntries),
      fairplayCertificateURL: this.getValueFromKey('fairplayCertificateURL', data.configEntries),
      gatewayClientId: this.getValueFromKey('gatewayClientId', data.configEntries),
      appStoreApple: this.getValueFromKey('appStoreApple', data.configEntries),
      appStoreGoogle: this.getValueFromKey('appStoreGoogle', data.configEntries),
      appStoreHuawei: this.getValueFromKey('appStoreHuawei', data.configEntries),
      gatewaySubscriptionKey: this.getValueFromKey('gatewaySubscriptionKey', data.configEntries),
      gatewaySignupRouterAuthFlow: this.getValueFromKey('gatewaySignupRouterAuthFlow', data.configEntries),
      gatewaySignupRouterUnauthFlow: this.getValueFromKey('gatewaySignupRouterUnauthFlow', data.configEntries),
      gatewayForgotPasswordAuthority: this.getValueFromKey('gatewayForgotPasswordAuthority', data.configEntries),
      settingsWebappManageProfileUrl: this.getValueFromKey('settingsWebappManageProfileUrl', data.configEntries),
      settingsWebappAccountSettingsUrl: this.getValueFromKey('settingsWebappAccountSettingsUrl', data.configEntries),
      heroAutomaticRollDelayMilliseconds: this.getValueFromKey('heroAutomaticRollDelayMilliseconds', data.configEntries),
      convivaCustomerKey: this.getValueFromKey('convivaCustomerKey', data.configEntries),
      myListTrayItemsLimit: this.getValueFromKey('myListTrayItemsLimit', data.configEntries),
      bookmarksTrayItemsLimit: this.getValueFromKey('bookmarksTrayItemsLimit', data.configEntries),
      gatewayPermittedPlaybackUserStatuses: this.getValueFromKey('gatewayPermittedPlaybackUserStatuses', data.configEntries),
      privacyPolicyEn: this.getValueFromKey('privacyPolicyEn', data.configEntries),
      privacyPolicyAr: this.getValueFromKey('privacyPolicyAr', data.configEntries),
      termsAndConditionsUrlEn: this.getValueFromKey('termsAndConditionsUrlEn', data.configEntries),
      termsAndConditionsUrlAr: this.getValueFromKey('termsAndConditionsUrlAr', data.configEntries),
      contactUsUrlEn: this.getValueFromKey('contactUsUrlEn', data.configEntries),
      contactUsUrlAr: this.getValueFromKey('contactUsUrlAr', data.configEntries),
      contactNumberUAE: this.getValueFromKey('contactNumberUAE', data.configEntries),
      contactNumberJOR: this.getValueFromKey('contactNumberJOR', data.configEntries),
      contactNumberBAH: this.getValueFromKey('contactNumberBAH', data.configEntries),
      contactNumberEGY: this.getValueFromKey('contactNumberEGY', data.configEntries),
      contactNumberKWT: this.getValueFromKey('contactNumberKWT', data.configEntries),
      contactNumberKSA: this.getValueFromKey('contactNumberKSA', data.configEntries),
      contactNumberQAT: this.getValueFromKey('contactNumberQAT', data.configEntries),
      seoFeedUrl: this.getValueFromKey('seoFeedUrl', data.configEntries),
      recommendationLearnVodPlayedPercentage: this.getValueFromKey('recommendationLearnVodPlayedPercentage', data.configEntries),
      mpxPersonUrlPrefix: this.getValueFromKey('mpxPersonUrlPrefix', data.configEntries),
      seasonCarouselShowSeasonNumber: this.getValueFromKey('seasonCarouselShowSeasonNumber', data.configEntries),
      paywallSubscriptionPrice: this.getValueFromKey('paywallSubscriptionPrice', data.configEntries),
      paywallSubscriptionCurrency: this.getValueFromKey('paywallSubscriptionCurrency', data.configEntries),
      showPopupForChurnedUsers: this.getValueFromKey('showPopupForChurnedUsers', data.configEntries),
      paywallGatewaySubscriptionPackageId: this.getValueFromKey('paywallGatewaySubscriptionPackageId', data.configEntries),
      searchAutocompleteLimit: this.getValueFromKey('searchAutocompleteLimit', data.configEntries),
      searchPageSizeDesktop: this.getValueFromKey('searchPageSizeDesktop', data.configEntries),
      searchPageSizeMobile: this.getValueFromKey('searchPageSizeMobile', data.configEntries),
      marketingHeroTrayScrollPeriodMilliseconds: this.getValueFromKey('marketingHeroTrayScrollPeriodMiliseconds', data.configEntries),
      trendingTraySwipeIntervalInMilliseconds: this.getValueFromKey('trendingTraySwipeIntervalInMilliseconds', data.configEntries),
      trendingTrayAutoSwipeShouldStartAfterInMilliseconds: this.getValueFromKey(
        'trendingTrayAutoSwipeShouldStartAfterInMilliseconds',
        data.configEntries
      ),
      marketingEmailCollectorScrollInterval: this.getValueFromKey('marketingEmailCollectorScrollInterval', data.configEntries),
      enableSeoDebugLogs: this.getValueFromKey('enableSeoDebugLogs', data.configEntries),
      continueWatchingTrayProgressMinimumPercent: this.getValueFromKey('continueWatchingTrayProgressMinimumPercent', data.configEntries),
      continueWatchingTrayProgressLimitPercent: this.getValueFromKey('continueWatchingTrayProgressLimitPercent', data.configEntries),
      gatewayAuthBaseUrl: this.getValueFromKey('gatewayAuthBaseUrl', data.configEntries),
      gatewaySignupFacadeBaseUrl: this.getValueFromKey('gatewaySignupFacadeBaseUrl', data.configEntries),
      playerBufferingGoal: this.getValueFromKey('playerBufferingGoal', data.configEntries),
      playerDefaultBitrate: this.getValueFromKey('playerDefaultBitrate', data.configEntries),
      seriesNextEpisodePlayDelay: this.getValueFromKey('seriesNextEpisodePlayDelay', data.configEntries),
      gatewayStreamingAppFacadeUrl: this.getValueFromKey('gatewayStreamingAppFacadeUrl', data.configEntries),
      gatewaySignupFunnelFacadeUrl: this.getValueFromKey('gatewaySignupFunnelFacadeUrl', data.configEntries),
      gatewaySignupLandingSupportFacadeUrl: this.getValueFromKey('gatewaySignupLandingSupportFacadeUrl', data.configEntries),
      mobileProviderSignUpSupportedCountries: this.getValueFromKey('mobileProviderSignUpSupportedCountries', data.configEntries),
      signUpWithMobileProviderLink: this.getValueFromKey('signUpWithMobileProviderLink', data.configEntries),
      footerMediaCenterTargetUrl: this.getValueFromKey('footerMediaCenterURL', data.configEntries),
      contactUsEmailAddress: this.getValueFromKey('contactUsEmailAddress', data.configEntries),
      addNewProfileUserStatuses: this.getValueFromKey('addNewProfileUserStatuses', data.configEntries),
      gatewaySubscriptionPackagePriorityList: this.getValueFromKey('gatewaySubscriptionPackagePriorityList', data.configEntries),
      mpxFeedUrlPrefix: this.getValueFromKey('mpxFeedUrlPrefix', data.configEntries),
      activeUserCtaPopupSubscriptionPackageId: this.getValueFromKey('activeUserCtaPopupSubscriptionPackageId', data.configEntries),
      activeUserAnnualCtaPopupMonthlyPackageIds: this.getValueFromKey('activeUserAnnualCtaPopupMonthlyPackageIds', data.configEntries),
      activeUserAnnualCtaPopupYearlyPackageIds: this.getValueFromKey('activeUserAnnualCtaPopupYearlyPackageIds', data.configEntries),
      settingsWebappPaymentSettingsUrl: this.getValueFromKey('settingsWebappPaymentSettingsUrl', data.configEntries),
      accountDeletionUrl: this.getValueFromKey('accountDeletionRedirectUrl', data.configEntries),
      touchstoneServiceUrl: this.getValueFromKey('touchstoneServiceUrl', data.configEntries),
      clevertapClientId: this.getValueFromKey('clevertapAccountId', data.configEntries),
      clevertapRegion: this.getValueFromKey('clevertapRegion', data.configEntries),
      clevertapTargetDomain: this.getValueFromKey('clevertapTargetDomain', data.configEntries),
      freeTrialTextEnabled: this.getValueFromKey('freeTrialTextEnabled', data.configEntries),
    };
  }

  private getValueFromKey(key: string, entries: ConfigEntryApiModel[]) {
    return entries.find((entry) => entry.key === key)?.value?.value;
  }
}
